import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Tab, Typography, IconButton, Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { horizontalTabStyles } from '../sharedStyles';
import FloatingMessage from '../../../common/FloatingMessage';
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
import CustomGrid from '../customgrid';
import EditDialog from '../customgrid/EditDialog';
import { LoadingIndicator } from '../reports/reconcilationreport/MuiStyled';
import { ADMIN_PERMISSIONS } from '../../../common/RolePermissions';
import PermissionButton from '../PermissionButton';

const editFields = [
    { name: 'request_automated', label: 'Request Automated', type: 'select', options: ['Yes', 'No'], required: true },
];

const useStyles = makeStyles(() => ({
    wrapper: {
        height: '100%',
        ...horizontalTabStyles.tabWrapper,
        '& .header': {
            padding: '10px 20px',
            fontSize: '1rem',
            fontWeight: 'bold'
        },
    },
    tabBody: {
        ...horizontalTabStyles.tabBody,
    },
}));

const FtpJiraSystems = ({ toggleError }) => {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        axios.get('/admin/ftp-jira-settings', {
            headers: { Authorization: authToken }
        }).then(res => {
            if (Array.isArray(res.data)) {
                setRows(res.data.map((item, i) => ({
                    id: i + 1,
                    app_name: item.app_name,
                    request_automated: item.request_automated ? 'Yes' : 'No',
                })));
            }
        }).catch(err => {
            console.log('Error fetching FTP/JIRA settings', err);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [authToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setEditDialogOpen(true);
    }, []);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const handleEditSave = useCallback((formValues) => {
        setIsLoading(true);
        axios.post('/admin/ftp-jira-settings', {
            app_name: editingRow.app_name,
            request_automated: formValues.request_automated === 'Yes',
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            setEditDialogOpen(false);
            setEditingRow(null);
            fetchData();
            toggleError('Settings updated successfully', 'success');
        }).catch(err => {
            const msg = err.response?.data?.message || 'Failed to update settings. Please try again.';
            toggleError(msg, 'error');
        }).finally(() => {
            setIsLoading(false);
        });
    }, [authToken, editingRow, fetchData, toggleError]);

    const gridData = useMemo(() =>
        rows.map((row, i) => ({ ...row, rownum: i + 1 })),
        [rows]
    );

    const columns = useMemo(() => {
        const cols = [
            { name: '#', mapping: 'rownum', width: 60, sortable: false, hideable: false, disableColumnMenu: true, align: 'center' },
            { name: 'Application Name', mapping: 'app_name', width: 250, sortable: false },
            { name: 'Request Automated', mapping: 'request_automated', width: 200, sortable: false, type: 'singleSelect', valueOptions: ['Yes', 'No'] },
        ];

            cols.push({
                name: 'Edit', mapping: 'actions_edit', width: 80, sortable: false, hideable: false, disableColumnMenu: true, align: 'center',
                renderCell: ({ row }) => (
                    <Tooltip title="Edit">
                        <PermissionButton iconOnly permission={ADMIN_PERMISSIONS.FTP_JIRA_SETTINGS} size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                            <Edit fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });

        return cols;
    }, [isAdmin, handleEditClick]);

    return (
        <div>
            {isLoading && <LoadingIndicator />}
            <CustomGrid
                columns={columns}
                rows={gridData}
                pageCount={10}
                showToolbar={false}
            />
            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={editFields}
                title="Edit FTP/JIRA Setting"
                columns={1}
            />
        </div>
    );
};

const FtpJiraSettings = ({ active }) => {
    const classes = useStyles();
    const [error, setError] = useState('');
    const [messageType, setMessageType] = useState('error');

    const toggleError = (msg, type = 'error') => {
        setError(msg);
        setMessageType(type);
    };

    return (
        <div className={classes.wrapper}>
            <FloatingMessage open={error !== ''} message={error} severity={messageType} onClose={() => setError('')} />
            <Typography variant="h6" className="header">
                FTP/JIRA SETTINGS
            </Typography>
            <Tabs
                variant="scrollable"
                aria-label="tabs"
                value={0}
            >
                <Tab label="FTP/JIRA Systems" />
            </Tabs>
            <div className={classes.tabBody}>
                <FtpJiraSystems toggleError={toggleError} />
            </div>
        </div>
    );
};

export default FtpJiraSettings;
